import {
  buildPromoLineHtml,
  promoScopeFor,
  readPromoScope,
  resolvePromo,
  stripPromoLineHtml,
} from '@theme/promo-rules';

const ANON_UI_KEY = 'constructor_anonymous_ui';

function getCustomerId() {
  try {
    const id = window.ShopifyAnalytics?.meta?.page?.customerId;
    return id ? String(id) : null;
  } catch {
    return null;
  }
}

function getOrCreateAnonId() {
  try {
    let id = localStorage.getItem(ANON_UI_KEY);
    if (!id) {
      id =
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(ANON_UI_KEY, id);
    }
    return id;
  } catch {
    return `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function getUserIdentifier() {
  return getCustomerId() || getOrCreateAnonId();
}

// ---------------------------------------------------------------------------
// Constructor behavioral tracking (recommendation view + click)
//
// This theme-native carousel talks to Constructor over raw HTTP — there is no
// Constructor JS SDK on the PDP — so we reproduce the two beacons the SDK's
// tracker fires. This replaces the tracking that lived in the removed React
// carousel (src/product-recommendations/hooks/useConstructorRecommendations.ts:
// trackRecommendationView / trackRecommendationClick). Endpoints, payload keys,
// and identity params mirror @constructor-io/constructorio-client-javascript
// v2.77.0 (modules/tracker.js):
//   POST /v2/behavioral_action/recommendation_result_view
//   POST /v2/behavioral_action/recommendation_result_click
// Identity params (key, i, s, ui, c, _dt) are read from the SAME cookie /
// localStorage the SDK uses (@constructor-io/constructorio-id), so these events
// attribute to the same client + session as SDK-fired events elsewhere on site.
// ---------------------------------------------------------------------------

const CONSTRUCTOR_SERVICE_URL = 'https://ac.cnstrc.com';
// Client-version tag (the SDK sends `ciojs-client-<v>`). A distinct value keeps
// these theme-native beacons identifiable in Constructor without affecting
// attribution, which is carried by key/i/s/ui.
const CONSTRUCTOR_CLIENT_VERSION = 'cio-theme-ymal-1.0.0';

// SDK identity storage keys (see @constructor-io/constructorio-id defaults:
// client id → cookie, session id → localStorage).
const CIO_CLIENT_ID_COOKIE = 'ConstructorioID_client_id';
const CIO_SESSION_ID_LOCAL = '_constructorio_search_session_id';
const CIO_SESSION_DATA_COOKIE = 'ConstructorioID_session';

function readCookie(name) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function generateUuidV4() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Mirror constructorio-id's fallback UUID format.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Reuse the SDK's client id (first-party cookie). If the SDK hasn't run yet on
// this page load, mint one in the SDK's UUID format and persist it to the same
// cookie so a later SDK load adopts it — keeping the visitor identity stable
// across both paths.
function getConstructorClientId() {
  try {
    let id = readCookie(CIO_CLIENT_ID_COOKIE);
    if (!id) {
      id = generateUuidV4();
      const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${CIO_CLIENT_ID_COOKIE}=${id}; expires=${expires}; path=/`;
    }
    return id;
  } catch {
    return generateUuidV4();
  }
}

// Read (never advance) the SDK's current session id so we don't interfere with
// its 30-minute session-rotation logic. Default to 1 — the SDK's own initial
// value — when no session exists yet.
function getConstructorSessionId() {
  try {
    const parsed = parseInt(localStorage.getItem(CIO_SESSION_ID_LOCAL), 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  } catch {
    /* localStorage blocked — fall through */
  }
  try {
    const raw = readCookie(CIO_SESSION_DATA_COOKIE);
    if (raw) {
      const sid = parseInt(JSON.parse(raw)?.sessionId, 10);
      if (Number.isFinite(sid) && sid > 0) return sid;
    }
  } catch {
    /* malformed cookie — fall through */
  }
  return 1;
}

function constructorIdentityParams(apiKey) {
  const params = {
    key: apiKey,
    i: getConstructorClientId(),
    s: getConstructorSessionId(),
    ui: getUserIdentifier(),
    c: CONSTRUCTOR_CLIENT_VERSION,
    _dt: Date.now(),
  };
  try {
    const { host, pathname } = window.location;
    if (host) params.origin_referrer = host + (pathname || '');
  } catch {
    /* no-op */
  }
  return params;
}

// Fire one behavioral beacon. Identity params ride the query string and are
// also merged into the JSON body (both, exactly as the SDK does). `beacon:true`
// prefers navigator.sendBeacon so a click that immediately navigates away still
// delivers; otherwise a keepalive fetch is used. Content-Type text/plain +
// mode cors keeps it a simple request (no preflight), matching the SDK.
function sendConstructorBeacon(action, apiKey, eventFields, { beacon = false } = {}) {
  if (!apiKey) return;
  try {
    const identity = constructorIdentityParams(apiKey);
    const url = new URL(`${CONSTRUCTOR_SERVICE_URL}/v2/behavioral_action/${action}`);
    Object.entries(identity).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });
    const body = JSON.stringify({ ...eventFields, ...identity });

    if (beacon && typeof navigator?.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain' });
      if (navigator.sendBeacon(url.toString(), blob)) return;
    }
    fetch(url.toString(), {
      method: 'POST',
      mode: 'cors',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
      body,
    }).catch(() => {
      /* tracking must never break the UI */
    });
  } catch (err) {
    console.warn('[You May Also Like] Tracking beacon failed:', err);
  }
}

// Mirrors useConstructorRecommendations.ts trackRecommendationView payload.
function trackRecommendationView({ apiKey, podId, resultId, resultCount, items }) {
  sendConstructorBeacon('recommendation_result_view', apiKey, {
    pod_id: podId,
    num_results_viewed: items.length,
    result_count: resultCount,
    result_page: 1,
    result_id: resultId,
    section: 'Products',
    url: window.location.href,
    items: items.map((item) => ({ item_id: item.itemId, item_name: item.itemName })),
  });
}

// Mirrors useConstructorRecommendations.ts trackRecommendationClick payload.
// Uses sendBeacon so the event survives the card-click navigation.
function trackRecommendationClick({ apiKey, podId, resultId, resultCount, numResultsPerPage, item }) {
  const fields = {
    pod_id: podId,
    strategy_id: podId,
    item_id: item.itemId,
    item_name: item.itemName,
    result_id: resultId,
    result_count: resultCount,
    result_page: 1,
    result_position_on_page: item.position,
    num_results_per_page: numResultsPerPage,
    section: 'Products',
  };
  if (item.variationId) fields.variation_id = item.variationId;
  sendConstructorBeacon('recommendation_result_click', apiKey, fields, { beacon: true });
}

// Collect swatch colors that aren't already present in this pod response. Their
// product ids come from each swatch's `from` gid; ids already in resultsById are
// skipped (their price/image is already available).
function collectMissingSwatchIds(targets, resultsById) {
  const ids = new Set();
  for (const target of targets) {
    // Bundle swatches name blazer children, never looked up (see findOwnSwatch).
    if (isBundleResultData(target?.result?.data)) continue;
    const swatches = target?.result?.data?.swatches;
    if (!Array.isArray(swatches)) continue;
    for (const swatch of swatches) {
      const id = swatchProductId(swatch);
      if (id && !resultsById.has(id)) ids.add(id);
    }
  }
  return Array.from(ids);
}

// Swatch colors outside this pod response carry no price/image here, so a swatch
// switch would fall back to the card's own price (a wrong per-color price).
// Batch-fetch those colors via Constructor's browse/items so each swatch
// resolves its real price. Best-effort: a chunk that fails is skipped and the
// existing card-price fallback still applies (AbortError re-throws so a
// superseded load bails cleanly).
async function fetchResultsByIds(ids, apiKey, signal) {
  const out = new Map();
  if (!Array.isArray(ids) || !ids.length || !apiKey) return out;
  const CHUNK = 50;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const url = new URL('https://ac.cnstrc.com/browse/items');
    url.searchParams.set('key', apiKey);
    url.searchParams.set('ui', getUserIdentifier());
    url.searchParams.set('num_results_per_page', String(chunk.length));
    for (const id of chunk) url.searchParams.append('ids', id);
    try {
      const resp = await fetch(url.toString(), { signal });
      if (!resp.ok) continue;
      const json = await resp.json();
      for (const r of json?.response?.results ?? []) {
        const id = String(r?.data?.id ?? r?.data?.product_id ?? '');
        if (id) out.set(id, r);
      }
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      // best-effort: skip this chunk
    }
  }
  return out;
}

function extractHandle(result) {
  const url = result?.data?.url || '';
  if (!url) return null;
  // The feed's `url` is sometimes a bare product handle (prod does this) —
  // mirror the React PLP's fallback before trying the /products/ pattern.
  if (/^[a-z0-9][a-z0-9._-]*$/i.test(url)) return url;
  // Match /products/<handle> at the start of the path; ignore query/hash.
  const match = url.match(/\/products\/([^/?#]+)/i);
  return match ? match[1] : null;
}

// Suit-bundle wrapper results. Constructor indexes each suit colourway as its
// own result (`id` = "<wrapper id>-<colour>", `product_id` = the wrapper) on
// the wrapper URL, with no variation_id and no inventory — wrappers never carry
// stock. Same signal as isBundleWrapperProduct in
// src/product-grid/api/mapConstructorProduct.ts.
function isBundleResultData(data) {
  if (!data) return false;
  const handle = (String(data.url || '').match(/\/products\/([^/?#]+)/i) || [])[1] || '';
  return (
    String(data.__type || '').toLowerCase() === 'bundle' ||
    String(data.product_type || data.productType || '').toLowerCase() === 'bundle' ||
    handle.toLowerCase().endsWith('-bundle') ||
    (data.variation_id == null &&
      data.variant_inventory_count == null &&
      Number(data.total_inventory_count) === 0)
  );
}

function colorSlug(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');
}

// Colour of a bundle result: the suffix of its id after the wrapper id.
function bundleResultColorSlug(data) {
  const id = String(data?.id ?? '');
  const rawProductId = Array.isArray(data?.product_id) ? data.product_id[0] : data?.product_id;
  const productId = String(rawProductId ?? '');
  if (productId && id.startsWith(`${productId}-`)) return id.slice(productId.length + 1);
  const match = id.match(/^\d+-(.+)$/);
  return match ? match[1] : '';
}

function withColorParam(url, slug) {
  if (!url || !slug) return url || '';
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('color', slug);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

// The swatch entry describing THIS result's own colourway. Regular results:
// the entry whose `from` gid is the result's product. Bundles: the entry whose
// name matches the colour in the result id — their `from` gids point at the
// BLAZER child products, so an id lookup would name a jacket's record.
function findOwnSwatch(result) {
  const data = result?.data || {};
  const raw = data.swatches;
  if (!Array.isArray(raw) || !raw.length) return null;
  if (isBundleResultData(data)) {
    const slug = bundleResultColorSlug(data);
    return (slug && raw.find((s) => colorSlug(s?.name) === slug)) || null;
  }
  const currentId = String(data.id ?? data.product_id ?? '');
  return (
    (currentId &&
      raw.find((s) => typeof s?.from === 'string' && s.from.endsWith(`/${currentId}`))) ||
    null
  );
}

// Price record for a result's own colourway: its swatch entry first — the
// per-colour source the PLP grid renders (src/product-grid/utils/priceSource.ts)
// — and the item record when the entry carries no price.
function resultPriceData(result) {
  const data = result?.data || {};
  const own = findOwnSwatch(result);
  if (own && own.current_price != null) {
    return {
      ...data,
      current_price: own.current_price,
      full_price: own.compare_at_price ?? null,
      compare_at_price: own.compare_at_price ?? null,
    };
  }
  return data;
}

function swatchTypedImage(swatch, type) {
  return (swatch?.images || []).find((img) => img && img.type === type && img.url) || null;
}

function swatchImageSrc(swatch, image) {
  return image?.url ? `${swatch?.baseUrl || ''}${image.url}` : '';
}

// Bundle cards never offer Shop Now / quick-shop — the card links through to
// the wrapper PDP (parity with the PLP grid and the Liquid card surfaces).
function stripQuickAdd(card) {
  card
    .querySelectorAll('quick-add-component, .quick-add__button, .product-card-media-footer__plus')
    .forEach((el) => el.remove());
  card.querySelectorAll('[data-product-card-click-action]').forEach((el) => {
    el.setAttribute('data-product-card-click-action', 'product_page');
  });
  card.querySelectorAll('[data-product-card-action]').forEach((el) => {
    el.setAttribute('data-product-card-action', 'product_page');
  });
}

// Horizon's subtitle block only emits `.custom-product-subtitle` when the
// product handle contains the colour (combined listings). Suit wrappers
// don't, so the wrapper is empty and a querySelector miss would leave the
// colour name off the card after we overlay Constructor data.
function ensureSubtitleEl(card) {
  let el = card.querySelector('.custom-product-subtitle');
  if (el) return el;
  const wrap = card.querySelector('.custom-product-subtitle-wrapper');
  if (!wrap) return null;
  el = document.createElement('span');
  el.className = 'custom-product-subtitle';
  wrap.appendChild(el);
  return el;
}

function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Build the swatch row HTML from a Constructor result. Mirrors the logic in
// src/product-grid/components/ProductCard.tsx:692 — pick `images.find(img => img.type === 'swatch')`
// per swatch entry, then `${baseUrl}${url}?width=50` as the thumbnail. Also wires up
// `data-image-url` for each swatch using `images.find(img => img.type === 'main')` so
// the theme's product-card.js hover handler swaps the gallery image (same idea as
// ProductCard.tsx:163's resolveSwatchHoverUrl).
function buildSwatchRowFromResult(result, parent, resultsById, discount) {
  const rawSwatches = result?.data?.swatches;
  if (!Array.isArray(rawSwatches) || rawSwatches.length === 0) return null;

  // Reorder swatches so the swatch matching THIS card's product sits at index 0.
  // The same swatches array is shared across cards in the family, so without
  // this every card's "active" defaults to whichever entry happens to sit at
  // index 0 — and hover-out reverts every card to that one product's image.
  // Mirrors PLP's logic in src/product-grid/components/ProductCard.tsx:420.
  const isBundle = isBundleResultData(result?.data);
  // Bundle swatches point (`from`) at blazer children: a sibling lookup by that
  // id returns the jacket's price/image/URL, so bundles never consult resultsById.
  const siblingsById = isBundle ? null : resultsById;
  let swatches = rawSwatches;
  const ownSwatch = findOwnSwatch(result);
  const ownIdx = ownSwatch ? rawSwatches.indexOf(ownSwatch) : -1;
  if (ownIdx > 0) {
    swatches = [rawSwatches[ownIdx], ...rawSwatches.slice(0, ownIdx), ...rawSwatches.slice(ownIdx + 1)];
  }

  const DESKTOP_LIMIT = 5;
  const MOBILE_LIMIT = 4;
  // Only swatches carrying a `type:"swatch"` thumbnail can render as a tile.
  // Filter the (already reordered) list to those renderable entries BEFORE
  // slicing — slicing first and then dropping image-less swatches inside the
  // map lets blanks in the first 5 positions silently eat visible slots (e.g.
  // the Jetsetter shirt showed 3 of 5 because positions 0 and 2 had no swatch
  // image, while valid swatches at positions 5+ never got a chance to render).
  // `filter` preserves order, so the reordered active swatch stays first if it
  // is renderable. Mirrors the PLP fix in
  // src/product-grid/components/ProductCard.tsx (renderableSwatches).
  const renderable = swatches.filter(
    (s) => s && (s.images || []).some((img) => img?.type === 'swatch' && img?.url)
  );
  if (renderable.length === 0) return null;
  const visible = renderable.slice(0, DESKTOP_LIMIT);

  const tiles = visible
    .map((swatch, i) => {
      if (!swatch) return '';
      const swatchThumb = (swatch.images || []).find((img) => img?.type === 'swatch');
      if (!swatchThumb?.url) return '';

      const thumbSep = swatchThumb.url.includes('?') ? '&' : '?';
      const thumbUrl = `${swatch.baseUrl || ''}${swatchThumb.url}${thumbSep}width=50`;

      const isActive = i === 0;

      // Per-colour price: the swatch entry's own current_price / compare_at_price
      // first — the same per-colour source the PLP grid renders
      // (src/product-grid/utils/priceSource.ts) — then the sibling colour's own
      // result when the entry is unpriced, then the card's price.
      const siblingResult = siblingsById && siblingsById.get(swatchProductId(swatch));
      const siblingData = siblingResult && siblingResult.data;
      // Offer scope follows the sibling colourway's own group_ids when known
      // (read the same way the card's are, so the active tile and the card
      // price can never be scoped from different fields). Unknown colourway:
      // sitewide offers only — colourways of one listing can be tagged for
      // different offers, so the card's scope is not its scope.
      const siblingGroupIds = siblingData?.group_ids ?? siblingResult?.group_ids;
      const swatchDiscount = Array.isArray(siblingGroupIds)
        ? promoScopeFor(discount, siblingGroupIds)
        : promoScopeFor(discount, []);
      const swatchPriceHtml =
        (swatch.current_price != null &&
          buildPriceHtml(
            { current_price: swatch.current_price, full_price: swatch.compare_at_price },
            swatchDiscount
          )) ||
        (siblingResult && buildPriceHtml(siblingResult.data || {}, swatchDiscount)) ||
        stripPromoLineHtml(parent.priceHtml);

      // Build a {main, hover} media set per swatch so the theme's gallery
      // update can replace BOTH the primary slide and the hover slide. Active
      // swatch points at the card's own image (parent.imageUrl /
      // parent.hoverImageUrl) so hover-out returns to it cleanly. For non-
      // active swatches whose product is also a top-level recommendation,
      // reuse that result's image_url so the clicked swatch matches the
      // sibling card's image exactly.
      const { main: mainSrc, hover: hoverSrc } = resolveSwatchImageUrls(
        swatch,
        isActive,
        parent,
        siblingsById,
        264,
        isBundle
      );
      const mediaJson = buildSwatchMediaJson(swatch, isActive, parent, siblingsById, isBundle);

      const swatchColor = swatchDisplayName(swatch);
      const subtitleText = isActive
        ? parent.subtitle || swatchColor
        : swatchColor || parent.subtitle;
      const productUrl = resolveSwatchProductUrl(swatch, isActive, parent, siblingsById, isBundle);
      const desktopOnly = i >= MOBILE_LIMIT ? ' product-recommendations__swatch-link--desktop-only' : '';

      return `
        <a
          href="${escapeHtml(productUrl)}"
          class="product-recommendations__swatch-link${isActive ? ' is-active' : ''}${desktopOnly}"
          aria-label="${escapeHtml(parent.title)}${subtitleText ? ' - ' + escapeHtml(subtitleText) : ''}"
          data-product-card-swatch
          data-product-url="${escapeHtml(productUrl)}"
          data-image-url="${escapeHtml(mainSrc)}"
          data-image-alt="${escapeHtml(parent.title)}"
          data-title-text="${escapeHtml(parent.title)}"
          data-subtitle-text="${escapeHtml(subtitleText)}"
          data-price-html="${escapeHtml(swatchPriceHtml)}"
          data-media-json="${escapeHtml(mediaJson)}"
        >
          <span class="swatch-wrapper">
            <span
              class="swatch swatch--unscaled"
              style="--swatch-background: url(${escapeHtml(thumbUrl)});"
            ></span>
          </span>
        </a>
      `;
    })
    .join('');

  let extras = '';
  if (renderable.length > DESKTOP_LIMIT) {
    extras += `<span class="product-recommendations__swatches-more product-recommendations__swatches-more--desktop">+${renderable.length - DESKTOP_LIMIT}</span>`;
  }
  if (renderable.length > MOBILE_LIMIT) {
    extras += `<span class="product-recommendations__swatches-more product-recommendations__swatches-more--mobile">+${renderable.length - MOBILE_LIMIT}</span>`;
  }
  return tiles + extras;
}

// Constructor only tags the swatch thumbnail with `type: "swatch"`. The main
// and hover images are returned untyped, identified by filename hints:
//   *_40_category-outfitter_* | *_40_outfitter_* | *_40_category_* | *_40_* → primary
//   *_90_hover_* | *_hover_*                                                 → hover/secondary
// In this catalog the canonical primary image (the one in result.data.image_url)
// always uses the `_40_*` prefix; lower-numbered prefixes (_1, _2, _3, _4) are
// detail/close-up shots which we don't want to surface on swatch click.
// (Confirmed by inspecting ac.cnstrc.com response for multiple products.)
function pickMainAndHoverFromSwatch(swatch) {
  const nonSwatch = (swatch?.images || []).filter((img) => img && img.type !== 'swatch');

  const matchUrl = (regex) => nonSwatch.find((img) => regex.test(img.url || ''));

  const hover = matchUrl(/_90_hover[_.]/i) || matchUrl(/_hover[_.\d]/i);

  // Priority order: most specific to least, _40_* always wins over generic.
  const matchMain = (regex) => nonSwatch.find((img) => img !== hover && regex.test(img.url || ''));
  const main =
    matchMain(/_40_category-outfitter[_.]/i) ||
    matchMain(/_40_outfitter[_.]/i) ||
    matchMain(/_40_category[_.]/i) ||
    matchMain(/_40_[a-z]/i) ||
    matchMain(/_category[-_]|_outfitter[_.]/i) ||
    nonSwatch.find((img) => img !== hover);

  return { main: main || null, hover: hover || null };
}

function absolutizeSwatchImage(swatch, image, width) {
  if (!image?.url) return '';
  const url = image.url;
  const sep = url.includes('?') ? '&' : '?';
  return `${swatch?.baseUrl || ''}${url}${sep}width=${width}`;
}

// Pull the Shopify product ID out of a swatch's `from` GID. Format:
// "gid://shopify/Product/<id>"
function swatchProductId(swatch) {
  const from = swatch?.from;
  if (typeof from !== 'string') return '';
  const parts = from.split('/');
  return parts[parts.length - 1] || '';
}

function relativeProductUrl(url) {
  if (!url) return '';
  // A bare handle (see extractHandle) becomes its canonical product URL.
  if (/^[a-z0-9][a-z0-9._-]*$/i.test(url)) return `/products/${url}`;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

function resolveSwatchProductUrl(swatch, isActive, parent, resultsById, isBundle) {
  if (isActive && parent.url) return parent.url;

  if (isBundle) {
    // Bundle swatches stay on the suit wrapper PDP; only ?color= changes.
    const base = swatch?.url ? relativeProductUrl(swatch.url) : parent.url || '';
    return withColorParam(base, colorSlug(swatch?.name)) || parent.url || '';
  }

  const sibling = resultsById?.get(swatchProductId(swatch));
  const siblingUrl = sibling?.data?.url;
  if (siblingUrl) return relativeProductUrl(siblingUrl);

  const siblingHandle = extractHandle(sibling);
  if (siblingHandle) return `/products/${siblingHandle}`;

  return parent.url || '';
}

// Resolve the canonical main + hover image URLs for a swatch. Priority:
//   1. If the swatch's product is itself a top-level Constructor result,
//      use that result's `image_url` / `hover_image_url` (these are the
//      same canonical marketing shots the result card renders). This is the
//      only path that guarantees clicking a swatch matches a sibling card.
//   2. Otherwise, pattern-match the swatch's own `images` array by filename.
function resolveSwatchImageUrls(swatch, isActive, parent, resultsById, width, isBundle) {
  if (isActive) {
    return {
      main: parent.imageUrl || '',
      hover: parent.hoverImageUrl || '',
    };
  }

  const sibling = resultsById?.get(swatchProductId(swatch));
  if (sibling) {
    return {
      main: sibling.data?.image_url || '',
      hover: sibling.data?.hover_image_url || '',
    };
  }

  if (isBundle) {
    // Bundle swatch entries arrive typed (main / hover / swatch); the filename
    // hints below exist for the untyped regular-catalog entries.
    const typedMain = swatchTypedImage(swatch, 'main');
    const typedHover = swatchTypedImage(swatch, 'hover');
    if (typedMain || typedHover) {
      return {
        main: absolutizeSwatchImage(swatch, typedMain, width) || parent.imageUrl || '',
        hover: absolutizeSwatchImage(swatch, typedHover, width) || parent.hoverImageUrl || '',
      };
    }
  }

  const { main, hover } = pickMainAndHoverFromSwatch(swatch);
  return {
    main: absolutizeSwatchImage(swatch, main, width) || parent.imageUrl || '',
    hover: absolutizeSwatchImage(swatch, hover, width) || parent.hoverImageUrl || '',
  };
}

// Build the media-json array the theme's #updateRecommendationGallery consumes.
// Shape per item: { id, src, alt } — matches snippets/product-card.liquid output.
function buildSwatchMediaJson(swatch, isActive, parent, resultsById, isBundle) {
  const { main, hover } = resolveSwatchImageUrls(swatch, isActive, parent, resultsById, 264, isBundle);
  const items = [];
  if (main) items.push({ id: 'main', src: main, alt: parent.title });
  if (hover) items.push({ id: 'hover', src: hover, alt: parent.title });
  return items.length ? JSON.stringify(items) : '';
}

// Catalog swatch image filenames in this store follow `swatch_<sku>_<color_words>.<ext>?v=...`.
// e.g. "swatch_20905-BLY09_pink_dune_plaid.jpg?v=..." → "Pink Dune Plaid".
function parseColorFromSwatchUrl(url) {
  if (!url) return '';
  const match = String(url).match(/swatch_[^_/]+_([^./?]+?)(?:\.(?:jpg|jpeg|png|webp|gif))/i);
  if (!match) return '';
  return match[1]
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

async function fetchCardHtml(handle, signal) {
  const url = `/products/${encodeURIComponent(handle)}?view=card`;
  const response = await fetch(url, { signal });
  const contentType = response.headers.get('content-type') || '';
  console.log(
    `[You May Also Like] Card fetch /products/${handle}?view=card → ${response.status} (${contentType})`
  );

  if (!response.ok) {
    throw new Error(`Card fetch ${response.status} for ${handle}`);
  }

  // Some stores may still respond with JSON if a `sections` param is implied; handle both.
  if (contentType.includes('application/json')) {
    const json = await response.json();
    const html = json?.card || Object.values(json || {})[0] || '';
    if (!html) {
      console.warn(`[You May Also Like] Card response had no card HTML for ${handle}.`, json);
    }
    return html;
  }

  const text = await response.text();
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const node = doc.querySelector('[data-product-card-fragment]');
  if (!node) {
    console.warn(
      `[You May Also Like] [data-product-card-fragment] not found in card response for ${handle}. Response length: ${text.length}. Verify templates/product.card.json is published and sections/product-card-fragment.liquid is uploaded.`
    );
    return '';
  }
  return node.outerHTML;
}

// B2: parse one fetched card fragment into a reusable template node, trimmed to
// the first two slides (main + hover) so each clone stays light.
function buildCardTemplate(html) {
  const tmpl = document.createElement('template');
  tmpl.innerHTML = html;
  const card = tmpl.content.firstElementChild;
  if (!card) return null;
  card
    .querySelectorAll('slideshow-slide[ref="slides[]"]')
    .forEach((slide, i) => {
      if (i >= 2) slide.remove();
    });
  card
    .querySelectorAll('[data-fit-pagination-segment]')
    .forEach((seg, i) => {
      if (i >= 2) seg.remove();
    });
  return card;
}

function fmtMoney(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

// Mirror the theme's price markup exactly so the card inherits its size (the
// product-price `h6` class) and the sale-price highlight color
// (`custom-price--highlight`). Building our own classes drops both.
function isOnSale(data) {
  const priceNum = Number(data?.current_price ?? data?.price);
  const compareNum = Number(data?.full_price ?? data?.compare_at_price);
  return Number.isFinite(compareNum) && Number.isFinite(priceNum) && compareNum > priceNum;
}

// Build the "$X with code CODE" promo line through the shared resolver so the
// markup (data-promo-slot picks the offer colour) and the winner-selection
// rules match snippets/price-discount-code-line.liquid exactly.
function buildDiscountLineHtml(currentPrice, scope) {
  if (!scope) return '';
  const price = Number(currentPrice);
  if (!Number.isFinite(price) || price <= 0) return '';
  const promo = resolvePromo(scope.rules, {
    priceCents: Math.round(price * 100),
    collectionHandles: scope.collectionHandles,
  });
  return promo ? buildPromoLineHtml(promo, fmtMoney(promo.discountedCents / 100)) : '';
}

function buildPriceHtml(data, discount) {
  const price = data?.current_price ?? data?.price;
  const compare = data?.full_price ?? data?.compare_at_price;
  const priceStr = fmtMoney(price);
  if (!priceStr) return '';
  const discountLine = buildDiscountLineHtml(price, discount);
  if (isOnSale(data)) {
    return (
      '<span class="price-group-wrapper">' +
      '<span role="group"><span class="visually-hidden">Regular price&nbsp;</span>' +
      `<span class="compare-at-price">${fmtMoney(compare)}</span></span> ` +
      '<span role="group"><span class="visually-hidden">Sale price&nbsp;</span>' +
      `<span class="price custom-price--highlight">${priceStr}</span></span>` +
      '</span>' +
      discountLine
    );
  }
  return (
    `<span class="price-group-wrapper"><span role="group"><span class="price">${priceStr}</span></span></span>` +
    discountLine
  );
}

// Display colour of one Constructor swatch entry. The entry's own `name` is
// the colour as merchandised ("The Khakis", "White", "Navy") — the same
// source the PLP grid renders (src/product-grid/utils/swatchIdentity.ts
// getSwatchDisplayName) and the cart recommendation renderers read. Only when
// the name is missing or not displayable (a bare number, a variant gid) fall
// back to parsing the swatch image filename, which only works for files named
// "swatch_<sku>_<colour>.jpg": files like "…_40_outfitter_swatch.jpg" or
// "…_swatch.jpg" parse to nothing, which is how the white / black / heather
// grey Soft Everyday Tee and the navy Performance Link Pants lost their colour
// line under "You May Also Like" while their siblings kept theirs.
function swatchDisplayName(swatch) {
  const name = String(swatch?.name ?? '').trim();
  if (name && !/^\d+$/.test(name) && !/ProductVariant\/\d+/.test(name)) return name;
  const thumb =
    (swatch?.images || []).find((img) => img?.type === 'swatch') ||
    (swatch?.images || [])[0];
  return thumb ? parseColorFromSwatchUrl(thumb.url) : '';
}

// Subtitle (color name) for a result — Constructor doesn't return a `color`
// field; it is the result's own swatch entry's display name (see
// swatchDisplayName). Mirrors the active-swatch resolution in
// buildSwatchRowFromResult.
function activeSwatchColor(result) {
  const raw = result?.data?.swatches;
  if (!Array.isArray(raw) || !raw.length) return '';
  return swatchDisplayName(findOwnSwatch(result) || raw[0]);
}

function withWidth(url, width) {
  if (!url) return '';
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}width=${width}`;
}

function setSlideImage(slide, src, alt) {
  if (!slide || !src) return;
  const img = slide.querySelector('img');
  if (!img) return;
  img.setAttribute('src', withWidth(src, 600));
  img.removeAttribute('srcset');
  img.setAttribute('alt', alt || '');
}

// Repopulate a cloned template card with a Constructor result's data so it
// reads as that product's card. Keeps the theme's exact markup (slideshow,
// PowerReviews rating hook, swatch styling) while swapping only the data.
function populateCardFromResult(card, result, resultsById, discount) {
  const data = result?.data || {};
  const isBundle = isBundleResultData(data);
  const id = String(data.id ?? data.product_id ?? '');
  const title = result?.value || data.title || '';
  const baseUrl = data.url
    ? relativeProductUrl(data.url)
    : `/products/${extractHandle(result) || ''}`;
  // Bundle results come one per colourway on the wrapper URL — carry the colour
  // so the PDP opens on it (the same link the PLP/search bundle cards use).
  const url = isBundle ? withColorParam(baseUrl, bundleResultColorSlug(data)) : baseUrl;
  const main = data.image_url || '';
  const hover = data.hover_image_url || '';

  // Product id everywhere — PowerReviews hook + gallery hover handler key off it.
  if (id) {
    card.querySelectorAll('[data-product-id]').forEach((el) => {
      el.dataset.productId = id;
    });
    const pr = card.querySelector('.power-reviews-star-rating-simple');
    if (pr) pr.setAttribute('data-id', id);
  }

  // Links + accessible labels.
  if (url) {
    card
      .querySelectorAll('a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]')
      .forEach((a) => a.setAttribute('href', url));
  }
  const galleryLink = card.querySelector('a[ref="cardGalleryLink"]');
  if (galleryLink && title) galleryLink.setAttribute('aria-label', title);
  const hiddenTitle = card.querySelector('a[ref="productCardLink"] .visually-hidden');
  if (hiddenTitle) hiddenTitle.textContent = title;

  // Title + subtitle. Set text on the inner heading/text-block leaf so the
  // theme's `text-block--*_product_title` styling (size) is preserved — setting
  // textContent on the `display:contents` anchor would strip that wrapper.
  const titleLeaf =
    card.querySelector('[ref="productTitleLink"] h1, [ref="productTitleLink"] h2, [ref="productTitleLink"] h3') ||
    card.querySelector('[ref="productTitleLink"] .text-block') ||
    card.querySelector('[ref="productTitleLink"]');
  if (titleLeaf) titleLeaf.textContent = title;
  const subtitleEl = ensureSubtitleEl(card);
  if (subtitleEl) subtitleEl.textContent = activeSwatchColor(result) || data.color || data.Color || '';

  // Price.
  const groupIds = data.group_ids || result?.group_ids || [];
  // Offer scope narrowed to this card's group_ids; each swatch below
  // re-narrows it to its own colourway's record, since a combined listing's
  // colours can sit in different collections.
  const scopeDiscount = promoScopeFor(discount, groupIds);
  const priceData = resultPriceData(result);
  const priceHtml = buildPriceHtml(priceData, scopeDiscount);
  const priceContainer =
    card.querySelector("product-price [ref='priceContainer']") ||
    card.querySelector('product-price');
  if (priceContainer && priceHtml) priceContainer.innerHTML = priceHtml;

  // Gallery: main + hover.
  const slides = card.querySelectorAll('slideshow-slide[ref="slides[]"]');
  setSlideImage(slides[0], main, title);
  setSlideImage(slides[1], hover || main, title);

  const badgesContainer = card.querySelector('.product-badges');
  if (badgesContainer) {
    badgesContainer.innerHTML = isOnSale(priceData)
      ? '<div class="product-badges__badge product-badges__badge--rectangle color-scheme-1">Selling Fast</div>'
      : '';
  }

  // Swatches from Constructor data (same logic as PLP). Suit-bundle
  // `?view=card` templates omit the row (handle has no colour suffix), so
  // inject one — same as collection-tabs-carousel-product cards.
  let swatchesContainer = card.querySelector('.product-recommendations__swatches');
  if (!swatchesContainer) {
    const host =
      card.querySelector('product-price')?.parentElement ||
      card.querySelector('.group-block-content') ||
      card.querySelector('.product-card__content');
    if (host) {
      swatchesContainer = document.createElement('div');
      swatchesContainer.className = 'product-recommendations__swatches';
      swatchesContainer.setAttribute('role', 'group');
      swatchesContainer.setAttribute('aria-label', 'Color');
      host.appendChild(swatchesContainer);
    }
  }
  if (swatchesContainer) {
    const parent = {
      title,
      subtitle: subtitleEl ? subtitleEl.textContent : '',
      priceHtml: priceHtml || '',
      url,
      imageUrl: main,
      hoverImageUrl: hover,
    };
    const row = buildSwatchRowFromResult(result, parent, resultsById, scopeDiscount);
    if (row) swatchesContainer.innerHTML = row;
  }

  if (isBundle) stripQuickAdd(card);
}

// Liquid-rendered pod cards (collection-tabs carousel) ship a bundle card with
// the wrapper product's admin price and featured image. Overlay the Constructor
// result's per-colour price, images and colour link, and drop quick-shop, so a
// bundle reads the same on every surface. No-op for non-bundle results.
function applyBundleCardOverrides(card, result, discount) {
  const data = result?.data || {};
  if (!card || !isBundleResultData(data)) return false;
  const title = result?.value || data.title || '';

  const priceData = resultPriceData(result);
  const priceHtml = buildPriceHtml(priceData, discount);
  const priceContainer =
    card.querySelector("product-price [ref='priceContainer']") ||
    card.querySelector('product-price');
  if (priceContainer && priceHtml) priceContainer.innerHTML = priceHtml;

  const own = findOwnSwatch(result);
  const main = data.image_url || swatchImageSrc(own, swatchTypedImage(own, 'main'));
  const hover = data.hover_image_url || swatchImageSrc(own, swatchTypedImage(own, 'hover'));
  const slides = card.querySelectorAll('slideshow-slide[ref="slides[]"]');
  if (main) setSlideImage(slides[0], main, title);
  if (hover) setSlideImage(slides[1], hover, title);

  const url = withColorParam(data.url ? relativeProductUrl(data.url) : '', bundleResultColorSlug(data));
  if (url) {
    card
      .querySelectorAll('a[href*="/products/"]:not([data-product-card-swatch])')
      .forEach((a) => a.setAttribute('href', url));
  }
  const subtitleEl = ensureSubtitleEl(card);
  if (subtitleEl) {
    subtitleEl.textContent = own?.name || activeSwatchColor(result) || data.color || data.Color || '';
  }

  stripQuickAdd(card);
  return true;
}

// --- Elevar rec tracking ---------------------------------------------------
// The Elevar app embed and the theme's grid tracker never see these
// Constructor-rendered recommendation cards, so this pod is invisible to the
// datalayer. Build the normalized product shape `window.BonobosElevar.map*`
// expects from a Constructor result, then fire `dl_view_item_list` (impression)
// and `dl_select_item` (click) with the Constructor pod id as the Elevar
// `list` value. This is additive to Constructor's own view/click tracking.
function buildElevarRecProduct(result) {
  const data = (result && result.data) || {};
  const productId = String(data.id != null ? data.id : data.product_id != null ? data.product_id : '');
  const handle = extractHandle(result);
  const url = data.url ? relativeProductUrl(data.url) : handle ? `/products/${handle}` : '';
  return {
    id: productId,
    product_id: productId,
    variant_id: '',
    name: (result && result.value) || data.title || '',
    brand: data.brand || data.vendor || '',
    // Real category from the Constructor feed's product_type when present.
    category: data.product_type || data.productType || data.type || '',
    variant: activeSwatchColor(result) || data.color || data.Color || '',
    price: data.current_price != null ? data.current_price : data.price,
    compare_at_price: data.full_price != null ? data.full_price : data.compare_at_price,
    image: data.image_url || '',
    url,
    quantity: '1',
  };
}

class ProductRecommendationsConstructor extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #loaded = false;
  #activeFetch = null;

  // Elevar rec-tracking state.
  #elevarPodId = '';
  #elevarEntries = new Map(); // slot element -> { product, position }
  #elevarTrackedIds = new Set();
  #elevarBatch = [];
  #elevarBatchTimer = null;
  #elevarObserver = null;
  #elevarClickBound = false;
  /**
   * @type {null | {
   *   apiKey: string,
   *   podId: string,
   *   resultId: string,
   *   resultCount: number,
   *   numResultsPerPage: number,
   *   itemsBySlot: Record<number, { itemId: string, itemName: string, variationId: string, position: number }>,
   * }}
   */
  #tracking = null;
  #clickBound = false;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
    this.#elevarObserver?.disconnect();
    clearTimeout(this.#elevarBatchTimer);
  }

  // Fire dl_view_item_list for recommendation cards as they cross 50% into view
  // (debounced batch), and dl_select_item when a card's product link is clicked.
  // `entries` is [{ slot, product, position }]; `list` is the Constructor pod id.
  #trackElevarRecs(entries, list) {
    if (!window.BonobosElevar || !entries || !entries.length) return;
    this.#elevarPodId = list;
    // Reset-safe: disconnect any prior observer and clear all state (incl. the
    // dedupe Set) before re-arming, so a future re-render doesn't leak the old
    // observer or suppress impressions. (Today load is one-shot, but keep this
    // consistent with the cart rec trackers.)
    this.#elevarObserver?.disconnect();
    this.#elevarObserver = null;
    this.#elevarEntries = new Map();
    this.#elevarTrackedIds = new Set();
    clearTimeout(this.#elevarBatchTimer);
    this.#elevarBatch = [];
    entries.forEach((en) =>
      this.#elevarEntries.set(en.slot, { product: en.product, position: en.position })
    );

    if (!this.#elevarClickBound) {
      this.#elevarClickBound = true;
      // Capture phase so it records before the browser follows the link.
      this.addEventListener('click', (e) => this.#handleElevarClick(e), true);
    }

    if ('IntersectionObserver' in window) {
      this.#elevarObserver = new IntersectionObserver(
        (obsEntries) => {
          let added = false;
          for (const oe of obsEntries) {
            if (!oe.isIntersecting) continue;
            this.#elevarObserver.unobserve(oe.target);
            const meta = this.#elevarEntries.get(oe.target);
            if (!meta) continue;
            const key = String(meta.product.product_id || meta.product.id || '');
            if (!key || this.#elevarTrackedIds.has(key)) continue;
            this.#elevarTrackedIds.add(key);
            this.#elevarBatch.push(meta);
            added = true;
          }
          if (added) this.#scheduleElevarFlush();
        },
        { threshold: 0.5 }
      );
      entries.forEach((en) => this.#elevarObserver.observe(en.slot));
    } else {
      this.#elevarBatch.push(...entries.map((en) => ({ product: en.product, position: en.position })));
      this.#flushElevarImpressions();
    }
  }

  #scheduleElevarFlush() {
    clearTimeout(this.#elevarBatchTimer);
    this.#elevarBatchTimer = setTimeout(() => this.#flushElevarImpressions(), 500);
  }

  #flushElevarImpressions() {
    const batch = this.#elevarBatch;
    this.#elevarBatch = [];
    clearTimeout(this.#elevarBatchTimer);
    this.#elevarBatchTimer = null;
    const el = window.BonobosElevar;
    if (!el || !batch.length) return;
    try {
      // Enrich each impressed product from its Shopify JSON (SKU/Vendor/Type/
      // Variant/ids the Constructor feed omits) before pushing dl_view_item_list.
      el.pushViewItemListEnriched(
        batch.map((m) => ({ product: m.product, position: m.position })),
        this.#elevarPodId,
      );
    } catch (_e) {
      /* Tracking must never break the UI. */
    }
  }

  #handleElevarClick(event) {
    const el = window.BonobosElevar;
    if (!el) return;
    try {
      const target = event.target;
      const link = target && target.closest ? target.closest('a[href*="/products/"]') : null;
      if (!link || !this.contains(link)) return;
      // Colour-swatch links preview/navigate to a sibling product, not a click
      // on this card's item — skip them.
      if (link.closest('.product-recommendations__swatch-link, [data-product-card-swatch]')) return;
      let node = link;
      let meta = null;
      while (node && node !== this) {
        if (this.#elevarEntries.has(node)) {
          meta = this.#elevarEntries.get(node);
          break;
        }
        node = node.parentElement;
      }
      if (!meta) return;
      // Enrich from Shopify (cache warmed by the impression fetch); never awaits,
      // so the click's navigation can't drop the event.
      el.pushSelectItemEnriched(meta.product, meta.position, this.#elevarPodId);
    } catch (_e) {
      /* Tracking must never break the UI. */
    }
  }

  async #loadRecommendations() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { apiKey, podId, productId, numResults } = this.dataset;
    if (!apiKey || !podId) {
      console.warn('[You May Also Like] Constructor not configured — apiKey or podId missing. Hiding section.');
      this.#hide();
      return;
    }

    const constructorUrl = this.#buildConstructorUrl({ apiKey, podId, productId, numResults });
    console.log('[You May Also Like] Fetching Constructor recommendations:', {
      podId,
      productId,
      numResults,
      url: constructorUrl,
    });

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const { signal } = this.#activeFetch;

    try {
      const response = await fetch(constructorUrl, { signal });
      if (!response.ok) throw new Error(`Constructor responded ${response.status}`);
      const json = await response.json();
      const results = json?.response?.results ?? [];
      console.log(`[You May Also Like] Constructor returned ${results.length} result(s).`, {
        resultId: json?.result_id,
        results,
      });

      // Index results by product ID so that, when a card's swatch points at
      // another result in this same response, we can swap to that result's
      // exact image_url/hover_image_url — the same shots its sibling card
      // displays. Without this the swatch falls back to filename-matching the
      // swatch's own `images` array, which sometimes lacks the canonical
      // marketing shot (only the result's image_url has it).
      const resultsById = new Map();
      for (const r of results) {
        const id = String(r?.data?.id ?? r?.data?.product_id ?? '');
        if (id) resultsById.set(id, r);
      }

      const handles = results
        .map((r) => ({ handle: extractHandle(r), result: r }))
        .filter((entry) => entry.handle);

      if (!handles.length) {
        console.warn('[You May Also Like] No usable product handles in Constructor results — hiding section.');
        this.#hide();
        return;
      }

      // Limit to the number of slots the section rendered.
      const slots = this.querySelectorAll('[data-rec-slot]');
      const limit = Math.min(handles.length, slots.length || handles.length);
      const targets = handles.slice(0, limit);

      // Give each swatch its real per-color price: merge any swatch colors
      // missing from this pod response into resultsById before rendering, so a
      // swatch switch shows that color's own price instead of the card's.
      try {
        const missingIds = collectMissingSwatchIds(targets, resultsById);
        if (missingIds.length) {
          const extra = await fetchResultsByIds(missingIds, apiKey, signal);
          for (const [id, r] of extra) if (!resultsById.has(id)) resultsById.set(id, r);
        }
      } catch (err) {
        if (err?.name === 'AbortError') return;
        // best-effort: render with the colors we already have
      }

      // B2: fetch ONE theme-rendered card as a template, then clone + repopulate
      // it from Constructor data for every recommendation. Turns N per-card
      // fetches into 1 while keeping the theme's exact card markup (section CSS,
      // slideshow, and PowerReviews rating hook all keep working).
      console.log(`[You May Also Like] Fetching 1 template card, cloning for ${targets.length}…`);
      let templateHtml = '';
      for (const entry of targets) {
        try {
          templateHtml = await fetchCardHtml(entry.handle, signal);
          if (templateHtml) break;
        } catch (err) {
          if (err?.name === 'AbortError') return;
        }
      }
      // Rec handles can 404 on this store (Constructor PROD ids that aren't
      // published here). The current PDP still has a working ?view=card —
      // clone that markup and repopulate it from Constructor.
      if (!templateHtml) {
        const pageHandle = (location.pathname.match(/\/products\/([^/?#]+)/i) || [])[1] || '';
        if (pageHandle) {
          try {
            templateHtml = await fetchCardHtml(pageHandle, signal);
          } catch (err) {
            if (err?.name === 'AbortError') return;
          }
        }
      }
      const template = templateHtml ? buildCardTemplate(templateHtml) : null;
      if (!template) {
        console.warn('[You May Also Like] Template card unavailable — hiding section.');
        this.#hide();
        return;
      }

      const discount = readPromoScope(this.dataset);

      // Per-slot item metadata for behavioral tracking, keyed by the slot's
      // `data-rec-slot` index so a click can look up exactly what was rendered
      // there. `trackedItems` (rendered order) feeds the view event.
      /** @type {Record<number, { itemId: string, itemName: string, variationId: string, position: number }>} */
      const itemsBySlot = {};
      /** @type {Array<{ itemId: string, itemName: string, variationId: string, position: number }>} */
      const trackedItems = [];
      // Elevar impression/click entries: { slot element, normalized product, 1-based position }.
      const elevarEntries = [];

      let filled = 0;
      targets.forEach((target, index) => {
        const slot = slots[index];
        if (!slot) return;
        const card = template.cloneNode(true);
        try {
          populateCardFromResult(card, target.result, resultsById, discount);
        } catch (err) {
          console.warn(`[You May Also Like] Failed to populate card for ${target.handle}:`, err);
          slot.remove();
          return;
        }
        slot.replaceChildren(card);

        const itemData = target.result?.data || {};
        const meta = {
          itemId: String(itemData.id ?? itemData.product_id ?? ''),
          itemName: target.result?.value || itemData.title || '',
          variationId: String(itemData.variation_id ?? ''),
          position: index + 1,
        };
        itemsBySlot[index] = meta;
        trackedItems.push(meta);

        // The Liquid card's inner slideshow-component renders with `disabled`
        // so it doesn't initialize before its parent card is in the DOM.
        // Removing the attribute + then setting it to "false" wakes it up so
        // touch swipe and the fit-pagination indicator become interactive.
        slot
          .querySelectorAll('a[ref="cardGalleryLink"] slideshow-component[disabled]')
          .forEach((slideshow) => {
            slideshow.removeAttribute('disabled');
            slideshow.setAttribute('disabled', 'false');
          });

        slot.classList.add('product-recommendations-constructor__slot--filled');
        elevarEntries.push({
          slot,
          product: buildElevarRecProduct(target.result),
          position: index + 1,
        });
        filled += 1;
      });

      // Remove any remaining unfilled placeholder slots (fewer results than max).
      for (let i = targets.length; i < slots.length; i += 1) {
        slots[i].remove();
      }

      if (filled === 0) {
        console.warn('[You May Also Like] No cards rendered — hiding section.');
        this.#hide();
        return;
      }

      console.log(`[You May Also Like] Rendered ${filled} card(s) from 1 template fetch.`);

      // Behavioral tracking: record what rendered so clicks can be attributed,
      // then fire the recommendation view event (parity with the removed React
      // carousel). Tracking failures never affect the rendered UI.
      this.#tracking = {
        apiKey,
        podId,
        resultId: json?.result_id || '',
        resultCount: results.length,
        numResultsPerPage: Number(numResults) || trackedItems.length,
        itemsBySlot,
      };
      this.#bindClickTracking();
      trackRecommendationView({
        apiKey,
        podId,
        resultId: this.#tracking.resultId,
        resultCount: results.length,
        items: trackedItems,
      });

      // Elevar rec tracking (dl_view_item_list impressions + dl_select_item
      // clicks) — additive to Constructor's own tracking above.
      this.#trackElevarRecs(elevarEntries, podId);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('[You May Also Like] Constructor recommendations error:', error);
      }
      this.#hide();
    } finally {
      this.#activeFetch = null;
    }
  }

  // Delegate clicks on any rendered card (product link, gallery, title, or a
  // swatch that navigates to a sibling product) to a single recommendation
  // click beacon, resolving which slot was clicked via `data-rec-slot`.
  #bindClickTracking() {
    if (this.#clickBound) return;
    this.#clickBound = true;
    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const anchor = target.closest('a');
        if (!anchor || !this.contains(anchor)) return;
        const slot = anchor.closest('[data-rec-slot]');
        if (!slot || !this.#tracking) return;
        const index = Number(slot.getAttribute('data-rec-slot'));
        const item = this.#tracking.itemsBySlot[index];
        if (!item || !item.itemId) return;
        trackRecommendationClick({
          apiKey: this.#tracking.apiKey,
          podId: this.#tracking.podId,
          resultId: this.#tracking.resultId,
          resultCount: this.#tracking.resultCount,
          numResultsPerPage: this.#tracking.numResultsPerPage,
          item,
        });
      },
      { capture: true }
    );
  }

  #buildConstructorUrl({ apiKey, podId, productId, numResults }) {
    const url = new URL(`https://ac.cnstrc.com/recommendations/v1/pods/${encodeURIComponent(podId)}`);
    url.searchParams.set('key', apiKey);
    url.searchParams.set('ui', getUserIdentifier());
    if (numResults) url.searchParams.set('num_results', numResults);
    if (productId) url.searchParams.set('item_id', productId);
    return url.toString();
  }

  #hide() {
    const list = this.querySelector('[data-has-recommendations]');
    if (list) list.dataset.hasRecommendations = 'false';
    this.classList.add('hidden');
    this.style.display = 'none';
  }
}

if (!customElements.get('product-recommendations-constructor')) {
  customElements.define('product-recommendations-constructor', ProductRecommendationsConstructor);
}

// Swatch-row machinery shared with assets/collection-tabs-carousel.js, which
// dynamic-imports this module so pod-tab cards render the exact same swatch
// set as these recommendation cards (importing is side-effect-safe: the only
// top-level effect is the guarded customElements.define above).
export {
  applyBundleCardOverrides,
  isBundleResultData,
  buildSwatchRowFromResult,
  collectMissingSwatchIds,
  fetchResultsByIds,
  readPromoScope,
  promoScopeFor,
  buildPriceHtml,
  isOnSale,
  activeSwatchColor,
  relativeProductUrl,
};
